<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:link',
	name: '$t:blocks.input_select_m2o.name',
	description: '$t:blocks.input_select_m2o.description',
	category: 'input',
	tags: ['form-control'],
	options: [
		{
			name: 'fieldId',
		},
		{
			name: 'referencedItemKey',
		},
		{
			name: 'disabled',
		},
		{
			name: 'readonly',
		},
		{
			name: 'disabledNotice',
		},
	],

	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { CompositePrimaryKey, GlobalSingleRelationFieldId } from '#layers/data-model';
import type { InputBlockProps } from '~/types/input-block';
import { computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { useOverlay } from '#imports';
import { defineBlock } from '#layers/block-engine';
import { getItemPrimaryKey } from '#layers/data-model/utils/primary-key';
import { useDisabledNotice } from '#layers/ui';
import { useDrawerFieldOverrides, useRelationFormField } from '~/composables/form-block';
import { injectFormBlockContext } from '~/composables/form-block/form-context';
import { getOriginalItemKey } from '~/composables/form-block/use-relation-field-state';
import { useM2OPermissions } from '~/composables/use-m2o-permissions';
import { useM2OState } from '~/composables/use-m2o-state';

interface InputSelectM2OBlockProps extends InputBlockProps {
	fieldId?: GlobalSingleRelationFieldId;
	referencedItemKey?: CompositePrimaryKey;
	showCreate?: boolean;
	showEdit?: boolean;
	editAllowed?: boolean;
	showRemove?: boolean;
}

const props = withDefaults(defineProps<InputSelectM2OBlockProps>(), {
	showCreate: true,
	showEdit: true,
	editAllowed: true,
	showRemove: true,
});

const LazyCollectionDetailDrawer = defineAsyncComponent(() => import('~/components/Collection/CollectionDetailDrawer.vue'));
const LazyCollectionListingDrawer = defineAsyncComponent(() => import('~/components/Collection/CollectionListingDrawer.vue'));

const { t } = useI18n();
const appConfig = useAppConfig();

const {
	field,
	referencedItemKey,
	emitConnect,
	emitDisconnect,
	emitCreate,
	emitUpdate,
	fieldValue,
	disabled,
	disabledNotice,
} = useRelationFormField<'to-one'>(props);
const fieldOverrides = useDrawerFieldOverrides(field, computed(() => field.value?.oppositeCollection ?? null));
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const { item, isCreate, isLoading } = useM2OState(field, referencedItemKey, fieldValue);
const formBlockContext = injectFormBlockContext(null);

// Permission-based UI controls
const permissions = useM2OPermissions({
	field,
	isCreateForm: () => formBlockContext?.isCreate.value ?? false,
});

const canEditItem = computed(() =>
	(!formBlockContext?.isCreate.value || isCreate.value)
	&& props.editAllowed
	&& permissions.canEdit.value,
);

// Allow viewing even when disabled - only block if creating a new parent item with unsaved nested item
const canViewItem = computed(() => !formBlockContext?.isCreate.value || isCreate.value);

;

const overlay = useOverlay();

async function handleSelect() {
	const modal = overlay.create(LazyCollectionListingDrawer, {
		props: {
			collectionId: field.value!.oppositeCollectionId,
			selection: item.value
				? [getItemPrimaryKey(item.value, field.value!.oppositeCollection!)]
				: [],
			selectionMode: 'single',
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result) {
		if (result.selection.length > 0) {
			emitConnect(result.selection[0]!);
		}
		else if (result.selection.length === 0 && referencedItemKey.value) {
			emitDisconnect(referencedItemKey.value);
		}
	}
}

function handleDeselect() {
	if (referencedItemKey.value) {
		emitDisconnect(referencedItemKey.value);
	}
}

async function handleCreate() {
	const modal = overlay.create(LazyCollectionDetailDrawer, {
		props: {
			collectionId: field.value!.oppositeCollectionId,
			title: t('common.create_item'),
			formMode: 'emit',
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result as { data: any } | undefined;

	if (payload) {
		emitCreate(payload.data);
	}
}

async function handleEdit() {
	const modal = useOverlay().create(LazyCollectionDetailDrawer, { props: {
		collectionId: field.value!.oppositeCollectionId,
		itemId: !isCreate.value ? getOriginalItemKey(item.value) : undefined,
		itemEdits: item.value ?? undefined,
		title: t('common.edit_item'),
		formMode: 'emit',
		fieldOverrides: fieldOverrides.value,
	} });
	const instance = modal.open();
	const payload = await instance.result as { data: any } | undefined;

	if (payload) {
		if (formBlockContext?.isCreate.value ?? false) {
			emitCreate(payload.data);
		}
		else {
			const itemKey = getOriginalItemKey(payload.data) ?? getItemPrimaryKey(payload.data, field.value!.oppositeCollection!);
			emitUpdate(itemKey, payload.data);
		}
	}
}

function handleView() {
	const modal = useOverlay().create(LazyCollectionDetailDrawer, { props: {
		collectionId: field.value!.oppositeCollectionId,
		itemId: !isCreate.value ? getOriginalItemKey(item.value) : undefined,
		itemEdits: item.value ?? undefined,
		title: t('common.view_item'),
	} });
	modal.open();
}
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DFieldGroup>
			<DTooltip :disabled="disabled || permissions.canConnect.value" :text="t('permissions.no_connect_access')">
				<DButton
					class="
						h-10 min-w-0 flex-1 pe-0.5 text-start font-normal text-default ring-default
						hover:bg-default hover:ring-accented
						focus-visible:ring-primary
					"
					color="neutral"
					variant="outline"
					data-test-id="select"
					:disabled="disabled || !permissions.canConnect.value"
					:readonly
					:aria-readonly="readonly"
					@click="handleSelect"
				>
					<div class="min-w-0 flex-1">
						<Transition
							enter-active-class="transition-opacity duration-50"
							enter-from-class="opacity-0"
							enter-to-class="opacity-100"
							leave-active-class="transition-opacity duration-50"
							leave-from-class="opacity-100"
							leave-to-class="opacity-0"
							mode="out-in"
						>
							<div v-if="isLoading">
								<DSkeleton class="h-4 w-64 rounded-sm opacity-0" />
							</div>
							<span v-else-if="!item" class="font-normal text-dimmed select-none">
								{{ t('common.select_item') }}
							</span>
							<RenderCollectionDisplayTemplate
								v-else
								class="truncate text-sm"
								:item
								:collection-id="field!.oppositeCollectionId"
							/>
						</Transition>
					</div>
					<DButton
						v-if="item && !props.readonly && props.showRemove"
						color="neutral"
						variant="link"
						data-test-id="remove"
						:icon="appConfig.ui.icons.remove"
						:disabled="disabled || !permissions.canDisconnect.value"
						class="rounded-sm!"
						@click.stop="handleDeselect"
					/>
				</DButton>
			</DTooltip>

			<DTooltip v-if="!item && showCreate" :disabled="disabled || !permissions.canCreate.value" :text="t('permissions.no_create_access')">
				<DButton
					class="flex w-10 justify-center ring-default"
					color="neutral"
					variant="outline"
					data-test-id="create"
					:icon="appConfig.ui.icons.plus"
					:disabled="disabled || !permissions.canCreate.value"
					:ui="{ leadingIcon: 'size-5' }"
					@click="handleCreate"
				/>
			</DTooltip>
			<!-- Edit button when user has edit permission -->
			<DTooltip v-else-if="item && showEdit && canEditItem" :disabled="canViewItem" text="This operation is currently not supported.">
				<DButton
					class="flex w-10 justify-center ring-default"
					color="neutral"
					variant="outline"
					data-test-id="edit"
					:icon="appConfig.ui.icons.edit"
					:disabled="!canViewItem"
					:ui="{ leadingIcon: 'size-5' }"
					@click="handleEdit"
				/>
			</DTooltip>
			<!-- View button when user can view but not edit -->
			<DButton
				v-else-if="item && showEdit && !canEditItem && canViewItem"
				class="flex w-10 justify-center ring-default"
				color="neutral"
				variant="outline"
				data-test-id="view"
				icon="lucide:eye"
				:ui="{ leadingIcon: 'size-5' }"
				@click="handleView"
			/>
		</DFieldGroup>
	</DTooltip>
</template>
